import moment from 'moment-timezone';

import { isPerfMetricsEnabled, measurePerf } from '../../debug/performance';

import { StockExchangeCodeType } from '../../models/fundamental/types';
import { InstrumentSnapshotMap } from '../../store/market-data/types';
import InitialSymbolsDataLoadingStatusCache from '../../store-util/InitialSymbolsDataLoadingStatusCache';
import SubscriptionTimingTracker from '../../store-util/SubscriptionTimingTracker';
import { updateInstrumentSnapshotCache } from '../../store-util/InstrumentSnapshotCache';
import { updateNBBOPricesCache } from '../../store-util/NBBOPriceCache';
import { updateStatsCache } from '../../store-util/StatsCache';
import { updateTradePriceCacheFromInstrumentSnapshot } from '../../store-util/TradePriceCache';
import { parseBase64String, parseRows } from '../../util/GatewayHelpers';
import { dataArrayToMap } from '../../util/MarketDataHelpers';
import { isEmpty } from '../../util/typeGuards';
import { WebSocketEventData } from '../custom-event/types';
import { LoggerService } from '../logger/Logger.service';
import { LATE_RESPONSE_THRESHOLD_MS } from '../web-socket/constants';
import { GatewayRequestType, GatewayResponseType } from '../web-socket/types';

import { BaseSubscriptionService } from './BaseSubscription.service';
import { SymbolInstrumentSnapshotSubscriptionRequestType } from './types';

const LOG_PREFIX = '[IS-SUBS]';
const logger = LoggerService.getLogger();

/**
 * Instrument Snapshot Subscription Service
 */
export class ISSubscriptionService extends BaseSubscriptionService<SymbolInstrumentSnapshotSubscriptionRequestType> {
  private static instance: ISSubscriptionService | undefined;

  // Make the constructor private, to have a single instance
   
  private constructor() {
    super();
  }

  static getInstance(): ISSubscriptionService {
    this.instance ??= new ISSubscriptionService();
    return this.instance;
  }

  public handleResponseEvent(event: WebSocketEventData<any>): void {
    const data = event.data ?? {};
    const { messageType: messageName, traceIdentifier, snapshots: snapshotsRaw } = data;
    const messageType: GatewayResponseType = GatewayResponseType[messageName] as GatewayResponseType ?? messageName;
    const traceIdentifierRequest = (this.gatewayService.getRequestedObjectList().find(
      item => item.messageType === this.getSubscriptionRequestType()
        && item.traceIdentifier === traceIdentifier,
    ) ?? {}) as SymbolInstrumentSnapshotSubscriptionRequestType;
    const { symbols, stockExchangeCode } = traceIdentifierRequest;
    // `symbols` is mutated as the subscription grows; `requestedSymbols` is what this request actually asked for.
    const requestedSymbols = traceIdentifierRequest.requestedSymbols ?? symbols;

    // Mark answered as soon as the response arrives - a parsing failure below is not a missing response.
    if (traceIdentifierRequest.traceIdentifier) {
      traceIdentifierRequest.respondedAt = moment.utc();

      if (traceIdentifierRequest.requestTime) {
        const elapsedMs = moment.utc().diff(traceIdentifierRequest.requestTime);
        if (elapsedMs > LATE_RESPONSE_THRESHOLD_MS) {
          logger.warn(`${LOG_PREFIX} Late InstrumentSnapshot response`, {
            traceIdentifier,
            elapsedMs,
            thresholdMs: LATE_RESPONSE_THRESHOLD_MS,
            stockExchangeCode,
            symbols,
          });
        }
      }
    }

    try {
      const snapshots = measurePerf('is.parseBase64String', undefined, () => parseBase64String(snapshotsRaw));
      const parsedData = measurePerf('is.parseRows', undefined, () => parseRows(snapshots.rows, snapshots));
      const finalData = measurePerf(
        'is.dataArrayToMap',
        { rows: parsedData?.length ?? 0 },
        () => dataArrayToMap(parsedData),
      );

      const symbolsReceived = Object.keys(finalData);

      if (symbolsReceived?.length !== requestedSymbols?.length) {
        const diff: string[] = [];
        (requestedSymbols as string[]).forEach(current => {
          if (!symbolsReceived.includes(current)) {
            diff.push(current);
          }
        });
        logger.warn(`${LOG_PREFIX} handleGWWebsocketMessages - symbols count for InstrumentSnapshot request and response differs - request:${requestedSymbols?.length} response:${symbolsReceived?.length} -- difference: ${diff}`, {
          symbols: requestedSymbols.concat().sort(),
          trackedSymbols: symbols?.concat().sort(),
          symbolsReceived,
          diff,
        });

        // Without lineClose these symbols never satisfy the InstrumentSnapshot gate, so re-request them.
        // Drop them from the cached request first, otherwise subscribeList treats them as already sent.
        if (!isEmpty(diff) && stockExchangeCode) {
          const cachedEntry = this.subscripitonRequests.find(item => item.traceIdentifier === traceIdentifier);
          if (cachedEntry) {
            cachedEntry.symbols = cachedEntry.symbols.filter(item => !diff.includes(item));
          }
          this.subscribeList(diff, stockExchangeCode);
        }
      }

      if (isPerfMetricsEnabled()) {
        SubscriptionTimingTracker.markPrice(symbolsReceived);
      }

      // eslint-disable-next-line import/no-named-as-default-member
      InitialSymbolsDataLoadingStatusCache.updateStatus('instrumentSnapshot', true);
      updateInstrumentSnapshotCache(finalData as InstrumentSnapshotMap, traceIdentifier);
      updateStatsCache(finalData as InstrumentSnapshotMap);
      updateTradePriceCacheFromInstrumentSnapshot(finalData as InstrumentSnapshotMap, stockExchangeCode);
      updateNBBOPricesCache(finalData as InstrumentSnapshotMap, 'instr');
    } catch (error) {
      logger.error(`${LOG_PREFIX} handleGWWebsocketMessages - error parsing '${messageType}' - ${error}`);
    }
  }

  public subscribe(
    symbol: string,
    stockExchangeCode: StockExchangeCodeType,
  ): void {
    const requestByBucketAndExchange = this.getRequestByBucketAndExchange(stockExchangeCode);

    if (!requestByBucketAndExchange) {
      const request: SymbolInstrumentSnapshotSubscriptionRequestType = {
        symbols: [ symbol ],
        stockExchangeCode,
        includePreMarket: true,
        includePostMarket: true,
        ...this.getDefaultRequestParams(),
      };
      this.sendRequestToGw(request);
      return;
    }

    if (requestByBucketAndExchange.symbols.includes(symbol)) { // This symbol already in the subscription request list!
      return;
    }

    requestByBucketAndExchange.symbols.push(symbol); // add symbol to related list
    this.gatewayService.sendRequest({
      symbols: [ symbol ],
      stockExchangeCode,
      includePreMarket: true,
      includePostMarket: true,
      ...this.getDefaultRequestParams(),
    });
  }

  public subscribeList(
    symbols: string[],
    stockExchangeCode: StockExchangeCodeType,
  ): void {
    const requestByBucketAndExchange = this.getRequestByBucketAndExchange(stockExchangeCode);

    if (!requestByBucketAndExchange) {
      const request = {
        symbols,
        stockExchangeCode,
        includePreMarket: true,
        includePostMarket: true,
        ...this.getDefaultRequestParams(),
      } as SymbolInstrumentSnapshotSubscriptionRequestType;
      this.sendRequestToGw(request);
      return;
    }

    const notRequestedList = symbols.filter(item => !requestByBucketAndExchange?.symbols.includes(item)) || [];

    if (isEmpty(notRequestedList)) { // All symbols are already added to subscription request list!
      return;
    }

    requestByBucketAndExchange.symbols = [ ...requestByBucketAndExchange?.symbols || [], ...notRequestedList ]; // add symbols to related list
    this.gatewayService.sendRequest({
      symbols: notRequestedList,
      stockExchangeCode,
      includePreMarket: true,
      includePostMarket: true,
      ...this.getDefaultRequestParams(),
    });
  }

  public unsubscribe(
    symbol: string,
    stockExchangeCode: StockExchangeCodeType,
  ): void {
    const requestByBucketAndExchange = this.getRequestByBucketAndExchange(stockExchangeCode);

    if (!requestByBucketAndExchange) { // There is no request in the list by the given exchange!
      return;
    }

    if (!requestByBucketAndExchange.symbols.includes(symbol)) { // There is no request in the list by the given symbol!
      return;
    }

    requestByBucketAndExchange.symbols = requestByBucketAndExchange.symbols.filter(item => item !== symbol); // remove only given symbol from the list
    this.gatewayService.sendRequest({
      ...this.getDefaultRequestParams(),
      includePreMarket: true,
      includePostMarket: true,
      symbols: [ symbol ],
      stockExchangeCode,
      messageType: this.getUnsubscriptionRequestType(),
    });
  }

  public unsubscribeList(
    symbols: string[],
    stockExchangeCode: StockExchangeCodeType,
  ): void {
    const requestByBucketAndExchange = this.getRequestByBucketAndExchange(stockExchangeCode);

    if (!requestByBucketAndExchange) { // There is no request in the list by the given exchange!
      return;
    }

    const requestedList = symbols.filter(item => requestByBucketAndExchange?.symbols.includes(item)) || [];

    if (isEmpty(requestedList)) { // The symbols are not in the subscription request list!
      return;
    }

    requestByBucketAndExchange.symbols = requestByBucketAndExchange.symbols.filter(
      item => !requestedList.includes(item),
    ); // remove given symbols from the list

    this.gatewayService.sendRequest({
      ...this.getDefaultRequestParams(),
      symbols: requestedList,
      stockExchangeCode,
      includePreMarket: true,
      includePostMarket: true,
      messageType: this.getUnsubscriptionRequestType(),
    });
  }

  protected shouldHandleResponse(event: WebSocketEventData<any>): boolean {
    return event.data?.messageType === this.getResponseType();
  }

  protected getResponseType(): GatewayResponseType {
    return GatewayResponseType.InstrumentSnapshot;
  }

  protected getSubscriptionRequestType(): GatewayRequestType {
    return GatewayRequestType.InstrumentSnapshot;
  }

  protected getUnsubscriptionRequestType(): GatewayRequestType {
    return GatewayRequestType.None;
  }

  private getRequestByBucketAndExchange(
    stockExchangeCode: StockExchangeCodeType,
  ): SymbolInstrumentSnapshotSubscriptionRequestType | undefined {
    return this.subscripitonRequests.find(
      item => item.stockExchangeCode === stockExchangeCode,
    );
  }
}
